/**
 * Opt-in, read-only diagnostics for Reading Deluxe locale investigations.
 * Enable with localStorage.setItem('flcReadingLocaleDebug', '1') + reload,
 * or append flcReadingLocaleDebug=1 to the URL (also works after #).
 * No Redux, network, audio, UI, or assessment side effects.
 */
const DEBUG_FLAG = 'flcReadingLocaleDebug';
const previous = new Map();

export const isReadingLocaleDebugEnabled = () => {
  try {
    return typeof window !== 'undefined' && (
      /(?:[?&#])flcReadingLocaleDebug=1(?:[&#]|$)/.test(window.location.href) ||
      window.localStorage?.getItem(DEBUG_FLAG) === '1'
    );
  } catch (error) {
    return false;
  }
};

export const getReadingRuntimeContext = () => {
  if (!isReadingLocaleDebugEnabled()) return null;
  const href = window.location.href;
  return {
    route: `${window.location.pathname}${window.location.hash.split('?')[0]}`,
    editorRoute: href.includes('edit-activities'),
    runtime: window.__FLC_MOBILE_APP__ || window.flutter_inappwebview
      ? 'mobile-webview-or-app' : 'web-or-preview',
  };
};

/** Capture the configured number and its exact per-locale text/audio fields. */
export const inspectReadingString = (strings, stringNumber, localeCode) => {
  if (!isReadingLocaleDebugEnabled()) return null;
  const hasId = stringNumber !== undefined && stringNumber !== null && String(stringNumber).trim() !== '';
  const record = hasId && Array.isArray(strings)
    ? strings.find(item => String(item?.stringNumber) === String(stringNumber))
    : null;
  const locales = record?.data || {};
  return {
    configuredId: hasId ? stringNumber : null,
    foundInCommonStrings: !!record,
    availableLocales: Object.keys(locales),
    requestedLocale: localeCode || null,
    requestedLocaleFields: locales[localeCode] || null,
    englishFields: locales.en || null,
  };
};

/** Safe logging only; skip repeated identical renders. */
export const logReadingLocale = (stage, payload) => {
  if (!isReadingLocaleDebugEnabled()) return;
  try {
    const snapshot = {
      stage,
      ...getReadingRuntimeContext(),
      ...payload,
    };
    const signature = JSON.stringify(snapshot);
    if (previous.get(stage) === signature) return;
    previous.set(stage, signature);
    const entry = JSON.parse(signature);
    // Developer-only bounded buffer for copying logs from a WebView console.
    const history = window.__FLC_READING_LOCALE_DIAGNOSTICS__ || [];
    history.push(entry);
    if (history.length > 80) history.shift();
    window.__FLC_READING_LOCALE_DIAGNOSTICS__ = history;
    console.info(`[FLC-READING-LOCALE][${stage}]`, entry);
  } catch (error) {
    // Diagnostics must never interrupt a lesson or its audio.
    console.warn('[FLC-READING-LOCALE][logging-failed]', stage, error?.message);
  }
};
